import { Injectable } from '@angular/core';
import { ApiExporterService } from '@domain/api-export';
import { QueryParamsProvider } from '@domain/routing';
import { BehaviorSubject, catchError, Observable, of, Subscription, switchMap, tap, throwError, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeService, DATE_TIME_FORMATTING, ErrorCodes, ToastDuration, ToastService } from '@shared';
import { SyncIntervalProvider } from './sync-interval.provider';
import { SyncStatus, SyncStatusEnum } from './sync-status.enum';
import { ApiConnectionsModel, WorkspaceRepository } from '@database/central';
import { ConnectionsManagerService } from '../connections/services';
import { ConnectionTypeEnum } from '../connections/models';
import { BusinessLayerService, IModal } from '@domain/business-layer';
import { LoggingService } from '@shared/logging';
import { HttpStatusCode } from '@angular/common/http';
import { ApiErrorModel } from '@api/http';

@Injectable({ providedIn: 'root' })
export class SyncService {
	public syncStatus$: Observable<SyncStatus>;
	private subscriptions: Subscription[];
	private syncStatusSubject: BehaviorSubject<SyncStatus> = new BehaviorSubject({
		status: SyncStatusEnum.NotStarted
	} as SyncStatus);

	// Production: 60 * 1000
	// Development/Testing: 2 * 1000
	private MINUTES_TO_MILLSECONDS_MULTIPLIER = 60 * 1000;

	public constructor(
		private syncIntervalProvider: SyncIntervalProvider,
		private apiExporterService: ApiExporterService,
		private queryParamsProvider: QueryParamsProvider,
		private toastService: ToastService,
		private translateService: TranslateService,
		private workspaceRepository: WorkspaceRepository,
		private connectionsManager: ConnectionsManagerService,
		private bll: BusinessLayerService,
		private loggingService: LoggingService,
		private dateTimeService: DateTimeService
	) {
		this.subscriptions = [];
		this.syncStatus$ = this.syncStatusSubject.asObservable();
	}

	public async startSynching() {
		this.stopSynching();

		if (!(await this.syncIntervalProvider.isSyncOn())) {
			this.stopSynching();
			return;
		}

		this.openConnectionsManager$().subscribe(async (modal: IModal | null) => {
			this.loggingService.info('SyncService', 'Start synching from connections manager subscription');
			if (modal && modal.data) {
				await this.workspaceRepository.setWorkfileConnectionId(this.queryParamsProvider.workfileId, modal.data);
			} else {
				await this.syncWorker();
			}
		});
	}

	private stopSynching() {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}

	private async syncWorker() {
		const syncIntervalInMinutes = await this.syncIntervalProvider.getIntervalInMinutes();

		const sync$ = timer(0, syncIntervalInMinutes * this.MINUTES_TO_MILLSECONDS_MULTIPLIER).pipe(
			switchMap(() => {
				this.loggingService.info('SyncService', 'Start sync');

				return this.workspaceRepository.getConnectionId$(this.queryParamsProvider.workfileId).pipe(
					switchMap((connectionId: string) =>
						this.workspaceRepository.isWorkfileImportedFromFile$(this.queryParamsProvider.workfileId).pipe(
							switchMap((isImportedFromFile: boolean) => {
								if (!this.queryParamsProvider.workfileId) {
									return this.handleNoWorkfileId();
								}

								if (!connectionId) {
									return this.handleNoConnectionId();
								}

								if (isImportedFromFile) {
									return this.handleWorkfileImportedFromFile();
								}

								this.syncStatusSubject.next({ status: SyncStatusEnum.InProgress });

								return this.apiExporterService
									.exportToFusion$(this.queryParamsProvider.workfileId)
									.pipe(
										tap(() => {
											this.syncStatusSubject.next({
												date: this.dateTimeService.getCurrentDateInFormat(DATE_TIME_FORMATTING),
												status: SyncStatusEnum.Synced
											});

											this.loggingService.info('SyncService', 'Export to fusion done');

											this.toastService.presentSuccess(
												this.translateService.instant('sync.success'),
												ToastDuration.Fast
											);
										})
									);
							}),
							catchError((error: string | ApiErrorModel) => {
								if (!(typeof error === 'string')) {
									this.handleNoInternetConnectionIfNeeded(error);
								} else {
									this.syncStatusSubject.next({
										date: this.dateTimeService.getCurrentDateInFormat(DATE_TIME_FORMATTING),
										status: SyncStatusEnum.Error
									});

									switch (error) {
										case ErrorCodes.NoActiveWorkfile:
											this.toastService.presentError(
												this.translateService.instant('sync.errorMessages.noActiveWorkfile'),
												ToastDuration.Slow
											);
											break;
										case ErrorCodes.NoConnectionAssociated:
											this.toastService.presentError(
												this.translateService.instant('sync.errorMessages.noConnectionId'),
												ToastDuration.Slow
											);
											break;
									}
								}
								return of(error);
							})
						)
					)
				);
			})
		);

		this.subscriptions.push(
			sync$.subscribe(() => {
				this.loggingService.info('SyncService', 'Sync finished');
			})
		);
	}

	private handleWorkfileImportedFromFile(): Observable<string> {
		this.loggingService.error(
			ErrorCodes.SyncFailed,
			'Workfile is imported from a JSON file, meaning that it cannot be synced.'
		);

		return throwError(() => ErrorCodes.NoActiveWorkfile);
	}

	private handleNoConnectionId(): Observable<string> {
		this.loggingService.error(ErrorCodes.SyncFailed, 'There was no connectionId for this workfileId.');

		return throwError(() => ErrorCodes.NoConnectionAssociated);
	}

	private handleNoWorkfileId(): Observable<string> {
		this.loggingService.error(ErrorCodes.SyncFailed, 'There was no workfileId in queryParamsProvider.');

		return throwError(() => ErrorCodes.NoActiveWorkfile);
	}

	private async handleNoInternetConnectionIfNeeded(error: ApiErrorModel) {
		this.loggingService.error(ErrorCodes.SyncFailed, error);

		this.syncStatusSubject.next({
			date: this.dateTimeService.getCurrentDateInFormat(DATE_TIME_FORMATTING),
			status: SyncStatusEnum.Error
		});

		const shouldShowNoInternetErrorMessages = await this.syncIntervalProvider.getNoInternetErrorMessage();

		if (error.httpStatusCode === HttpStatusCode.ServiceUnavailable && shouldShowNoInternetErrorMessages) {
			this.toastService.presentError(this.translateService.instant(error.errorMessage), ToastDuration.Slow);
		} else {
			this.toastService.presentError(
				this.translateService.instant(error.errorMessage),
				ToastDuration.Slow
			);
		}
	}

	private openConnectionsManager$(): Observable<IModal | null> {
		const workfileId = this.queryParamsProvider.workfileId;
		if (!workfileId) {
			throwError(() => ErrorCodes.WorkfileDoesNotExist);
			return of(null);
		}

		return this.workspaceRepository.isWorkfileImportedFromFile$(workfileId).pipe(
			switchMap((isImportedFromWorkfile: boolean) => {
				if (isImportedFromWorkfile) {
					return of(null);
				}

				return this.bll.getConnectionModel$(workfileId).pipe(
					switchMap((apiConnectionModel?: ApiConnectionsModel) => {
						if (!apiConnectionModel?.selectedOptions) {
							return this.connectionsManager.open(ConnectionTypeEnum.includeOptions);
						} else {
							return of(null);
						}
					})
				);
			})
		);
	}
}
